// src/components/ActivityQuestionsModal.jsx
import { useState, useEffect, useCallback } from "react";
import { X, Plus, Trash2, GripVertical, Info } from "lucide-react";

export default function ActivityQuestionsModal({
  isOpen,
  onClose,
  onSubmit,
  parentId,
  selectedQuestion,
  onSwitchToAI,
  toAbsoluteUrlA
}) {
  const [pregunta, setPregunta] = useState("");
  const [tipo, setTipo] = useState("seleccion_multiple");
  // const [respuestaCorrecta, setRespuestaCorrecta] = useState("");
  const [explicacion, setExplicacion] = useState("");
  const [puntos, setPuntos] = useState(1);
  const [tiempo, setTiempo] = useState("");
  const [mediaRemoved, setMediaRemoved] = useState(false);

  // === NUEVO: Adjuntos ===
  const [mediaType, setMediaType] = useState("");            // '', 'image', 'audio'
  const [mediaFile, setMediaFile] = useState(null);          // File | null
  const [mediaPreview, setMediaPreview] = useState("");      // objectURL para preview
  const [mediaAlt, setMediaAlt] = useState("");              // alt/descripcion para imagen
  const [mediaCaption, setMediaCaption] = useState("");      // pie de media (opcional)

  // Específicos de tipos
  const [opcionesMultiple, setOpcionesMultiple] = useState([
    { id: 1, texto: "", esCorrecta: false },
    { id: 2, texto: "", esCorrecta: false }
  ]);
  const [respuestaVerdaderoFalso, setRespuestaVerdaderoFalso] = useState("verdadero");
  const [opcionesOrdenamiento, setOpcionesOrdenamiento] = useState([
    { id: 1, texto: "", orden: 1 },
    { id: 2, texto: "", orden: 2 }
  ]);
  const [paresEmparejamiento, setParesEmparejamiento] = useState([
    { id: 1, columnaA: "", columnaB: "" },
    { id: 2, columnaA: "", columnaB: "" }
  ]);
  const [textosCompletar, setTextosCompletar] = useState({
    textoConEspacios: "",
    respuestasCorrectas: [""]
  });


  const absUrl = (u) =>
    typeof toAbsoluteUrlA === "function" ? toAbsoluteUrlA(u) : u

  const getQuestionMedia = (q) => q?.media || q?.opciones?.media || null;

  const loadQuestionData = useCallback((question) => {
    setPregunta(question.pregunta || question.enunciado || question.nombre || "");

    // RESP_CORTA OFF: si viene 'respuesta_corta', forzamos a 'seleccion_multiple'
    const incomingType = question.tipo_pregunta || "seleccion_multiple";
    setTipo(incomingType === "respuesta_corta" ? "seleccion_multiple" : incomingType);

    setExplicacion(question.explicacion || "");
    setPuntos(question.puntos || 1);
    setTiempo(
      question.tiempo_limite_segundos != null ? String(question.tiempo_limite_segundos) : ""
    );

    // === NUEVO: leer media desde opciones.media o question.media
    const m = getQuestionMedia(question);
    if (m?.type) {
      setMediaType(m.type); // 'image' | 'audio'
      setMediaAlt(m.alt || "");
      setMediaCaption(m.caption || "");
      if (m.url) {
        const abs = absUrl(m.url);
        setMediaPreview(abs);
      } else {
        setMediaPreview("");
      }
      setMediaFile(null);
      setMediaRemoved(false);
    } else {
      clearMedia();
      setMediaRemoved(false);
    }

    setTimeout(() => {
      // RESP_CORTA OFF: nunca cargar específicos de 'respuesta_corta'
      const t = (question.tipo_pregunta || "seleccion_multiple");
      loadTipoSpecificData(t === "respuesta_corta" ? "seleccion_multiple" : t, question);
    }, 0);
  }, []);

  const resetForm = useCallback(() => {
    setPregunta("");
    setTipo("seleccion_multiple");
    //setRespuestaCorrecta("");
    setExplicacion("");
    setPuntos(1);
    setTiempo("");
    resetTipoSpecificFields();
    clearMedia(); // === NUEVO
    setMediaRemoved(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }
    if (selectedQuestion) {
      loadQuestionData(selectedQuestion);
    } else {
      resetForm();
    }
  }, [isOpen, selectedQuestion, loadQuestionData, resetForm]);

  const loadTipoSpecificData = (tipoDetectado, question) => {
    switch (tipoDetectado) {
      case "seleccion_multiple":
        if (question.opciones?.opciones) {
          const opciones = question.opciones.opciones;
          const correctas = question.respuesta_correcta?.correctas || [];
          const opcionesConCorrectas = opciones.map(o => ({
            id: o.id || Math.random(),
            texto: o.texto || "",
            esCorrecta: correctas.includes(o.id)
          }));
          while (opcionesConCorrectas.length < 2) {
            opcionesConCorrectas.push({
              id: Math.max(...opcionesConCorrectas.map(o => o.id), 0) + 1,
              texto: "",
              esCorrecta: false
            });
          }
          setOpcionesMultiple(opcionesConCorrectas);
        }
        break;
      case "verdadero_falso":
        if (question.respuesta_correcta?.respuesta) {
          setRespuestaVerdaderoFalso(question.respuesta_correcta.respuesta);
        }
        break;
      case "ordenamiento":
        if (question.opciones?.opciones && question.respuesta_correcta?.orden_correcto) {
          const opciones = question.opciones.opciones;
          const ordenCorrecto = question.respuesta_correcta.orden_correcto;
          const opcionesOrdenadas = ordenCorrecto.map((id, index) => {
            const opcion = opciones.find(o => o.id === id);
            return { id, texto: opcion?.texto || "", orden: index + 1 };
          });
          while (opcionesOrdenadas.length < 2) {
            const newId = Math.max(...opcionesOrdenadas.map(o => o.id), 0) + 1;
            opcionesOrdenadas.push({ id: newId, texto: "", orden: opcionesOrdenadas.length + 1 });
          }
          setOpcionesOrdenamiento(opcionesOrdenadas);
        }
        break;
      case "emparejamiento":
        if (question.opciones?.columna_a && question.opciones?.columna_b) {
          const columnaA = question.opciones.columna_a;
          const columnaB = question.opciones.columna_b;
          const pares = columnaA.map((itemA, index) => ({
            id: itemA.id || index + 1,
            columnaA: itemA.texto || "",
            columnaB: columnaB[index]?.texto || ""
          }));
          while (pares.length < 2) {
            pares.push({ id: Math.max(...pares.map(p => p.id), 0) + 1, columnaA: "", columnaB: "" });
          }
          setParesEmparejamiento(pares);
        }
        break;
      case "completar":
        if (question.opciones?.texto_con_espacios && question.respuesta_correcta?.respuestas) {
          const textoCompleto = {
            textoConEspacios: question.opciones.texto_con_espacios,
            respuestasCorrectas: question.respuesta_correcta.respuestas || [""]
          };
          if (textoCompleto.respuestasCorrectas.length === 0) {
            textoCompleto.respuestasCorrectas = [""];
          }
          setTextosCompletar(textoCompleto);
        }
        break;
      /* RESP_CORTA OFF:
      case "respuesta_corta":
      default:
        if (question.respuesta_correcta?.respuesta) {
          setRespuestaCorrecta(question.respuesta_correcta.respuesta);
        }
        break;
      */
      default:
        // Sin acción para tipos no soportados
        break;
    }
  };

  useEffect(() => {
    if (!selectedQuestion) {
      resetTipoSpecificFields();
    }
  }, [tipo, selectedQuestion]);

  const headerText = selectedQuestion ? "Editar Pregunta" : "Agregar Pregunta";

  const resetTipoSpecificFields = () => {
    setOpcionesMultiple([
      { id: 1, texto: "", esCorrecta: false },
      { id: 2, texto: "", esCorrecta: false }
    ]);
    setRespuestaVerdaderoFalso("verdadero");
    setOpcionesOrdenamiento([
      { id: 1, texto: "", orden: 1 },
      { id: 2, texto: "", orden: 2 }
    ]);
    setParesEmparejamiento([
      { id: 1, columnaA: "", columnaB: "" },
      { id: 2, columnaA: "", columnaB: "" }
    ]);
    setTextosCompletar({ textoConEspacios: "", respuestasCorrectas: [""] });
  };

  // === NUEVO: Adjuntos – helpers/stubs ===
  const clearMedia = () => {
    if (mediaPreview && mediaPreview.startsWith("blob:")) {
      URL.revokeObjectURL(mediaPreview);
    }
    setMediaType("");
    setMediaFile(null);
    setMediaPreview("");
    setMediaAlt("");
    setMediaCaption("");
  };

  const handleRemoveMediaClick = () => {
    // Indica intención de quitar adjunto existente
    setMediaRemoved(true);
    clearMedia();
  };

  const handlePickMedia = (file) => {
    if (!file) { clearMedia(); return; }

    const mime = (file.type || "").toLowerCase();
    const isImg = mime.startsWith("image/");
    const isAud = mime.startsWith("audio/");
    if (!isImg && !isAud) {
      console.warn("[AQM] Archivo no válido. Solo imagen/audio.");
      return;
    }
    // Si no hay chip seleccionado o no coincide, lo fijamos al MIME
    if (!mediaType || (mediaType === "image" && !isImg) || (mediaType === "audio" && !isAud)) {
      setMediaType(isImg ? "image" : "audio");
    }

    setMediaFile(file);
    if (mediaPreview && mediaPreview.startsWith("blob:")) URL.revokeObjectURL(mediaPreview);
    setMediaPreview(URL.createObjectURL(file));
    setMediaRemoved(false);
  };

  // Función placeholder para que implementes subida/guardado donde corresponda
  const uploadMediaPlaceholder = async () => {
    // TODO: Implementar subida al backend/S3 y devolver { url, storageKey, ... }
    // return { url: 'https://...', storageKey: '...' }
    return null;
  };

  // Selección múltiple
  const agregarOpcionMultiple = () => {
    const nuevaId = Math.max(...opcionesMultiple.map(o => o.id)) + 1;
    setOpcionesMultiple([...opcionesMultiple, { id: nuevaId, texto: "", esCorrecta: false }]);
  };
  const eliminarOpcionMultiple = (id) => {
    if (opcionesMultiple.length > 2) {
      setOpcionesMultiple(opcionesMultiple.filter(o => o.id !== id));
    }
  };
  const actualizarOpcionMultiple = (id, campo, valor) => {
    setOpcionesMultiple(opcionesMultiple.map(o => (o.id === id ? { ...o, [campo]: valor } : o)));
  };

  // Ordenamiento
  const agregarOpcionOrdenamiento = () => {
    const nuevaId = Math.max(...opcionesOrdenamiento.map(o => o.id)) + 1;
    const nuevoOrden = opcionesOrdenamiento.length + 1;
    setOpcionesOrdenamiento([...opcionesOrdenamiento, { id: nuevaId, texto: "", orden: nuevoOrden }]);
  };
  const eliminarOpcionOrdenamiento = (id) => {
    if (opcionesOrdenamiento.length > 2) {
      const nuevas = opcionesOrdenamiento.filter(o => o.id !== id);
      setOpcionesOrdenamiento(nuevas.map((o, idx) => ({ ...o, orden: idx + 1 })));
    }
  };
  const actualizarOpcionOrdenamiento = (id, texto) => {
    setOpcionesOrdenamiento(opcionesOrdenamiento.map(o => (o.id === id ? { ...o, texto } : o)));
  };

  // Emparejamiento
  const agregarParEmparejamiento = () => {
    const nuevaId = Math.max(...paresEmparejamiento.map(p => p.id)) + 1;
    setParesEmparejamiento([...paresEmparejamiento, { id: nuevaId, columnaA: "", columnaB: "" }]);
  };
  const eliminarParEmparejamiento = (id) => {
    if (paresEmparejamiento.length > 2) {
      setParesEmparejamiento(paresEmparejamiento.filter(p => p.id !== id));
    }
  };
  const actualizarParEmparejamiento = (id, campo, valor) => {
    setParesEmparejamiento(paresEmparejamiento.map(p => (p.id === id ? { ...p, [campo]: valor } : p)));
  };

  // Completar
  const agregarRespuestaCompletar = () => {
    setTextosCompletar({ ...textosCompletar, respuestasCorrectas: [...textosCompletar.respuestasCorrectas, ""] });
  };
  const eliminarRespuestaCompletar = (index) => {
    if (textosCompletar.respuestasCorrectas.length > 1) {
      setTextosCompletar({
        ...textosCompletar,
        respuestasCorrectas: textosCompletar.respuestasCorrectas.filter((_, i) => i !== index)
      });
    }
  };
  const actualizarRespuestaCompletar = (index, valor) => {
    setTextosCompletar({
      ...textosCompletar,
      respuestasCorrectas: textosCompletar.respuestasCorrectas.map((r, i) => (i === index ? valor : r))
    });
  };

  const handleSave = () => {
    let opcionesFinales = {};
    let respuestaCorrectaFinal = "";

    switch (tipo) {
      case "seleccion_multiple":
        opcionesFinales = {
          opciones: opcionesMultiple.map(o => ({ texto: o.texto, id: o.id }))
        };
        respuestaCorrectaFinal = { correctas: opcionesMultiple.filter(o => o.esCorrecta).map(o => o.id) };
        break;
      case "verdadero_falso":
        opcionesFinales = { opciones: [{ texto: "Verdadero", valor: "verdadero" }, { texto: "Falso", valor: "falso" }] };
        respuestaCorrectaFinal = { respuesta: respuestaVerdaderoFalso };
        break;
      case "ordenamiento":
        opcionesFinales = { opciones: opcionesOrdenamiento.map(o => ({ texto: o.texto, id: o.id })) };
        respuestaCorrectaFinal = {
          orden_correcto: [...opcionesOrdenamiento].sort((a, b) => a.orden - b.orden).map(o => o.id)
        };
        break;
      case "emparejamiento":
        opcionesFinales = {
          columna_a: paresEmparejamiento.map(p => ({ id: p.id, texto: p.columnaA })),
          columna_b: paresEmparejamiento.map(p => ({ id: p.id, texto: p.columnaB }))
        };
        respuestaCorrectaFinal = { pares_correctos: paresEmparejamiento.map(p => ({ a: p.id, b: p.id })) };
        break;
      case "completar":
        opcionesFinales = {
          texto_con_espacios: textosCompletar.textoConEspacios,
          numero_espacios: textosCompletar.respuestasCorrectas.length
        };
        respuestaCorrectaFinal = { respuestas: textosCompletar.respuestasCorrectas };
        break;
      /* RESP_CORTA OFF:
      case "respuesta_corta":
      default:
        opcionesFinales = {};
        respuestaCorrectaFinal = { respuesta: respuestaCorrecta };
        break;
      */
      default:
        // Nada que agregar para tipos no soportados
        break;
    }

    if (!mediaFile && mediaType && mediaPreview && !mediaRemoved) {
      opcionesFinales = {
        ...opcionesFinales,
        media: {
          type: mediaType,
          alt: mediaAlt || null,
          caption: mediaCaption || null,
          url: mediaPreview
        }
      };
    }

    const isBank = !parentId;

    const basePayload = {
      ...(selectedQuestion && { id: selectedQuestion.id }),
      pregunta,
      tipo_pregunta: tipo,
      opciones: opcionesFinales,
      respuesta_correcta: respuestaCorrectaFinal,
      explicacion,
      puntos,
      tiempo_limite_segundos: tiempo ? Number(tiempo) : null,
      activo: true,

      // Campos específicos para multipart (el service los detecta)
      media_type: mediaType || null,
      media_alt: mediaAlt || "",
      media_caption: mediaCaption || "",
      media_file: mediaFile || null,
      remove_media: mediaRemoved && !mediaFile
    };


    // 👇 Solo estructura cuando hay parentId; si no, es banco.
    const payload = isBank ? basePayload : { estructura_id: parentId, ...basePayload };

    onSubmit(payload);
    onClose();
  };

  const renderTipoSpecificFields = () => {
    switch (tipo) {
      case "seleccion_multiple":
        return (
          <div className="form-group">
            <label>Opciones de respuesta</label>
            <div className="opciones-container">
              {opcionesMultiple.map((opcion, index) => (
                <div key={opcion.id} className="opcion-item">
                  <div className="opcion-numero">{index + 1}</div>
                  <input
                    type="text"
                    placeholder="Texto de la opción"
                    value={opcion.texto}
                    onChange={(e) => actualizarOpcionMultiple(opcion.id, 'texto', e.target.value)}
                    className="opcion-input"
                  />
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={opcion.esCorrecta}
                      onChange={(e) => actualizarOpcionMultiple(opcion.id, 'esCorrecta', e.target.checked)}
                    />
                    <span>Correcta</span>
                  </label>
                  {opcionesMultiple.length > 2 && (
                    <button type="button" onClick={() => eliminarOpcionMultiple(opcion.id)} className="btn-eliminar">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={agregarOpcionMultiple} className="btn-agregar">
                <Plus size={16} /> Agregar opción
              </button>
            </div>
          </div>
        );
      case "verdadero_falso":
        return (
          <div className="form-group">
            <label>Respuesta correcta</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="verdadero_falso"
                  value="verdadero"
                  checked={respuestaVerdaderoFalso === "verdadero"}
                  onChange={(e) => setRespuestaVerdaderoFalso(e.target.value)}
                />
                <span>Verdadero</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="verdadero_falso"
                  value="falso"
                  checked={respuestaVerdaderoFalso === "falso"}
                  onChange={(e) => setRespuestaVerdaderoFalso(e.target.value)}
                />
                <span>Falso</span>
              </label>
            </div>
          </div>
        );
      case "ordenamiento":
        return (
          <div className="form-group">
            <label>Elementos a ordenar</label>
            <p className="help-text">Los estudiantes deberán ordenar estos elementos en el orden correcto</p>
            <div className="opciones-container">
              {opcionesOrdenamiento.map((opcion) => (
                <div key={opcion.id} className="opcion-item">
                  <div className="grip-handle"><GripVertical size={16} /></div>
                  <div className="orden-numero">#{opcion.orden}</div>
                  <input
                    type="text"
                    placeholder="Elemento a ordenar"
                    value={opcion.texto}
                    onChange={(e) => actualizarOpcionOrdenamiento(opcion.id, e.target.value)}
                    className="opcion-input"
                  />
                  {opcionesOrdenamiento.length > 2 && (
                    <button type="button" onClick={() => eliminarOpcionOrdenamiento(opcion.id)} className="btn-eliminar">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={agregarOpcionOrdenamiento} className="btn-agregar">
                <Plus size={16} /> Agregar elemento
              </button>
            </div>
          </div>
        );
      case "emparejamiento":
        return (
          <div className="form-group">
            <label>Pares a emparejar</label>
            <p className="help-text">Los estudiantes deberán emparejar elementos de la columna A con la columna B</p>
            <div className="emparejamiento-container">
              <div className="columnas-header">
                <div className="columna-header">Columna A</div>
                <div className="columna-header">Columna B</div>
                <div className="acciones-header">Acciones</div>
              </div>
              {paresEmparejamiento.map((par) => (
                <div key={par.id} className="par-item">
                  <input
                    type="text"
                    placeholder="Elemento A"
                    value={par.columnaA}
                    onChange={(e) => actualizarParEmparejamiento(par.id, 'columnaA', e.target.value)}
                    className="par-input"
                  />
                  <input
                    type="text"
                    placeholder="Elemento B"
                    value={par.columnaB}
                    onChange={(e) => actualizarParEmparejamiento(par.id, 'columnaB', e.target.value)}
                    className="par-input"
                  />
                  {paresEmparejamiento.length > 2 && (
                    <button type="button" onClick={() => eliminarParEmparejamiento(par.id)} className="btn-eliminar">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={agregarParEmparejamiento} className="btn-agregar">
                <Plus size={16} /> Agregar par
              </button>
            </div>
          </div>
        );
      case "completar":
        return (
          <div className="form-group">
            <label>Texto con espacios en blanco</label>
            <p className="help-text">Usa _____ (5 guiones bajos) para marcar los espacios.</p>
            <textarea
              value={textosCompletar.textoConEspacios}
              onChange={(e) => setTextosCompletar({ ...textosCompletar, textoConEspacios: e.target.value })}
              rows={3}
              placeholder="Ej: El _____ es el planeta más cercano al _____."
            />
            <label>Respuestas correctas (en orden)</label>
            <div className="respuestas-container">
              {textosCompletar.respuestasCorrectas.map((respuesta, index) => (
                <div key={index} className="respuesta-item">
                  <div className="respuesta-numero">#{index + 1}</div>
                  <input
                    type="text"
                    placeholder="Respuesta correcta"
                    value={respuesta}
                    onChange={(e) => actualizarRespuestaCompletar(index, e.target.value)}
                    className="respuesta-input"
                  />
                  {textosCompletar.respuestasCorrectas.length > 1 && (
                    <button type="button" onClick={() => eliminarRespuestaCompletar(index)} className="btn-eliminar">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={agregarRespuestaCompletar} className="btn-agregar">
                <Plus size={16} /> Agregar respuesta
              </button>
            </div>
          </div>
        );
      /* RESP_CORTA OFF:
      case "respuesta_corta":
        return (
          <div className="form-group">
            <label>Respuesta correcta</label>
            <input
              type="text"
              value={respuestaCorrecta}
              onChange={(e) => setRespuestaCorrecta(e.target.value)}
              placeholder="Ingresa la respuesta correcta"
            />
          </div>
        );
      */
      default:
        return null;
    }
  };


  if (!isOpen) return null;

  return (
    <div className="aqm-overlay" onClick={onClose}>
      <div className="aqm-content" onClick={e => e.stopPropagation()}>
        <div className="aqm-header">
          <h2>{headerText}</h2>
          <div className="header-actions">

            <button className="aqm-close" onClick={onClose}><X /></button>
          </div>
        </div>

        <div className="aqm-body">
          <div className="form-group">
            <label>Pregunta</label>
            <textarea
              value={pregunta}
              onChange={e => setPregunta(e.target.value)}
              rows={3}
              placeholder="Escribe tu pregunta aquí..."
            />
          </div>

          <div className="form-group">
            <label>Tipo de pregunta</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)}>
              {/* <option value="respuesta_corta">Respuesta corta</option> */}
              <option value="seleccion_multiple">Selección múltiple</option>
              <option value="verdadero_falso">Verdadero/Falso</option>
              <option value="completar">Completar espacios</option>
              <option value="ordenamiento">Ordenamiento</option>
              <option value="emparejamiento">Emparejamiento</option>
            </select>
          </div>

          {renderTipoSpecificFields()}

          {/* === NUEVO: Apartado de adjuntos (opcional) === */}
          <div className="form-group">
            <label>Adjunto (opcional)</label>
            <div className="media-switch">
              <button
                type="button"
                className={`media-chip ${mediaType === "image" ? "active" : ""}`}
                onClick={() => { if (mediaType !== "image") { clearMedia(); setMediaType("image"); setMediaRemoved(false); } }}
              >
                Imagen
              </button>
              <button
                type="button"
                className={`media-chip ${mediaType === "audio" ? "active" : ""}`}
                onClick={() => { if (mediaType !== "audio") { clearMedia(); setMediaType("audio"); setMediaRemoved(false); } }}
              >
                Audio
              </button>
              <button className="media-chip ghost" onClick={handleRemoveMediaClick}>Quitar adjunto</button>
            </div>

            {mediaType === "image" && (
              <div className="media-card">
                <div className="media-row">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePickMedia(e.target.files?.[0])}
                  />
                </div>
                {mediaPreview ? (
                  <div className="media-preview">
                    {/* Solo preview local si se seleccionó archivo o si vino url en edición */}
                    <img src={mediaPreview} alt={mediaAlt || "Vista previa"} style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid #e5e7eb" }} />
                  </div>
                ) : null}
                <div className="media-row two">
                  <input
                    type="text"
                    placeholder="Texto alternativo (accesibilidad)"
                    value={mediaAlt}
                    onChange={(e) => setMediaAlt(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Pie de imagen (opcional)"
                    value={mediaCaption}
                    onChange={(e) => setMediaCaption(e.target.value)}
                  />
                </div>
              </div>
            )}

            {mediaType === "audio" && (
              <div className="media-card">
                <div className="media-row">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => handlePickMedia(e.target.files?.[0])}
                  />
                </div>
                {mediaPreview ? (
                  <div className="media-preview">
                    <audio controls src={mediaPreview} style={{ width: "100%" }} />
                  </div>
                ) : null}
                <div className="media-row">
                  <input
                    type="text"
                    placeholder="Descripción del audio (opcional)"
                    value={mediaCaption}
                    onChange={(e) => setMediaCaption(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Stub para futura subida */}
            {/* <button type="button" className="btn-agregar" onClick={uploadMediaPlaceholder}>Subir adjunto (stub)</button> */}
          </div>

          <div className="form-group">
            <label>Explicación (Pista pregunta)</label>
            <textarea
              value={explicacion}
              onChange={e => setExplicacion(e.target.value)}
              rows={2}
              placeholder="En esta parte colocarás la pista de la pregunta..."
            />
          </div>

          {/* SECCIÓN DE AUDITORÍA PARA PREGUNTAS */}
            {selectedQuestion && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '24px', borderTop: '1px solid #E2E8F0', paddingTop: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#475569' }}>Creado por</label>
                  <div style={{ backgroundColor: '#F1F5F9', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', color: '#64748B', fontSize: '14px' }}>
                    {selectedQuestion?.creador_email || 'admin_facultad@utm.edu.ec'}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#475569' }}>Fecha de creación</label>
                  <div style={{ backgroundColor: '#F1F5F9', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', color: '#64748B', fontSize: '14px' }}>
                    {selectedQuestion?.created_at ? new Date(selectedQuestion.created_at).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES')}
                  </div>
                </div>

                {selectedQuestion && (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#475569' }}>Editado por</label>
                      <div style={{ backgroundColor: '#F1F5F9', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', color: '#64748B', fontSize: '14px' }}>
                        {selectedQuestion?.editor_email || 'admin_facultad@utm.edu.ec'}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#475569' }}>Fecha de edición</label>
                      <div style={{ backgroundColor: '#F1F5F9', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', color: '#64748B', fontSize: '14px' }}>
                        {selectedQuestion?.updated_at ? new Date(selectedQuestion.updated_at).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES')}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
        </div>

        <div className="aqm-footer">
          <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          <button className="btn-submit" onClick={handleSave}>
            {selectedQuestion ? "Guardar Cambios" : "Guardar Pregunta"}
          </button>
        </div>

        <style>{`
          .aqm-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1rem;}
          .aqm-content{background:#fff;border-radius:12px;width:100%;max-width:700px;max-height:90vh;box-shadow:0 20px 40px rgba(0,0,0,.3);display:flex;flex-direction:column;overflow:hidden;}
          .aqm-header{padding:1.5rem;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;background:#f8fafc;}
          .header-actions{display:flex;align-items:center;gap:.5rem;}
          .btn-switch{background:#fbbf24;color:#fff;border:none;padding:.5rem 1rem;border-radius:6px;font-size:.875rem;cursor:pointer;transition:.2s;}
          .btn-switch:hover{background:#f59e0b;}
          .aqm-close{background:none;border:none;cursor:pointer;padding:.5rem;border-radius:6px;color:#6b7280;transition:.2s;}
          .aqm-close:hover{background:#f3f4f6;color:#374151;}
          .aqm-body{padding:1.5rem;display:flex;flex-direction:column;gap:1.5rem;overflow-y:auto;flex:1;}
          .form-group{display:flex;flex-direction:column;gap:.5rem;}
          .form-row{display:grid;grid-template-columns:2fr 1fr 1fr;gap:1rem;}
          .form-group label{font-weight:600;color:#374151;font-size:.875rem;}
          .help-text{font-size:.75rem;color:#6b7280;margin:0;font-style:italic;}
          .form-group textarea,.form-group input,.form-group select{width:100%;padding:.75rem;border:2px solid #e5e7eb;border-radius:6px;font-size:.875rem;transition:.2s;}
          .form-group textarea:focus,.form-group input:focus,.form-group select:focus{outline:none;border-color:#3b82f6;box-shadow:0 0 0 3px rgba(59,130,246,.1);}

          .opciones-container{display:flex;flex-direction:column;gap:.75rem;}
          .opcion-item{display:flex;align-items:center;gap:.75rem;padding:.75rem;border:1px solid #e5e7eb;border-radius:6px;background:#f8fafc;}
          .opcion-numero{background:#3b82f6;color:#fff;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.75rem;font-weight:600;flex-shrink:0;}
          .orden-numero{background:#10b981;color:#fff;padding:.25rem .5rem;border-radius:4px;font-size:.75rem;font-weight:600;flex-shrink:0;}
          .grip-handle{color:#9ca3af;cursor:grab;flex-shrink:0;}
          .grip-handle:active{cursor:grabbing;}
          .opcion-input,.par-input,.respuesta-input{flex:1;padding:.5rem;border:1px solid #d1d5db;border-radius:4px;font-size:.875rem;}
          .checkbox-label,.radio-label{display:flex;align-items:center;gap:.5rem;font-size:.875rem;cursor:pointer;}
          .radio-group{display:flex;gap:1rem;}
          .btn-eliminar{background:#fef2f2;color:#dc2626;border:1px solid #fecaca;padding:.5rem;border-radius:4px;cursor:pointer;transition:.2s;flex-shrink:0;}
          .btn-eliminar:hover{background:#fee2e2;}
          .btn-agregar{background:#f0f9ff;color:#0369a1;border:1px solid #bae6fd;padding:.75rem;border-radius:6px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:.5rem;font-weight:500;transition:.2s;}
          .btn-agregar:hover{background:#e0f2fe;}

          .emparejamiento-container{display:flex;flex-direction:column;gap:.5rem;}
          .columnas-header{display:grid;grid-template-columns:1fr 1fr auto;gap:.75rem;padding:.5rem 0;border-bottom:1px solid #e5e7eb;}
          .columna-header,.acciones-header{font-weight:600;color:#374151;font-size:.875rem;text-align:center;}
          .par-item{display:grid;grid-template-columns:1fr 1fr auto;gap:.75rem;align-items:center;padding:.5rem;border:1px solid #e5e7eb;border-radius:6px;background:#f8fafc;}

          .respuestas-container{display:flex;flex-direction:column;gap:.75rem;}
          .respuesta-item{display:flex;align-items:center;gap:.75rem;padding:.5rem;border:1px solid #e5e7eb;border-radius:6px;background:#f8fafc;}
          .respuesta-numero{background:#10b981;color:#fff;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.75rem;font-weight:600;flex-shrink:0;}

          /* === NUEVO: estilos de adjuntos === */
          .media-switch{display:flex;gap:.5rem;flex-wrap:wrap;}
          .media-chip{border:1px solid #e5e7eb;background:#fff;color:#374151;padding:.4rem .75rem;border-radius:999px;cursor:pointer;font-size:.85rem;}
          .media-chip.active{border-color:#c7d2fe;background:#eef2ff;color:#3730a3;font-weight:600;}
          .media-chip.ghost{background:#f9fafb;border-style:dashed;}
          .media-card{border:1px solid #e5e7eb;border-radius:8px;background:#f9fafb;padding:.75rem;display:flex;flex-direction:column;gap:.75rem;}
          .media-row{display:flex;gap:.75rem;align-items:center;flex-wrap:wrap;}
          .media-row.two > input{flex:1;}
          .media-preview{padding:.25rem 0;}

          .aqm-footer{padding:1rem 1.5rem;border-top:1px solid #e5e7eb;display:flex;justify-content:flex-end;gap:.75rem;background:#f8fafc;}
          .btn-cancel{background:#f3f4f6;color:#374151;border:1px solid #d1d5db;padding:.75rem 1.5rem;border-radius:6px;cursor:pointer;font-weight:500;transition:.2s;}
          .btn-cancel:hover{background:#e5e7eb;}
          .btn-submit{background:#10b981;color:#fff;border:none;padding:.75rem 1.5rem;border-radius:6px;cursor:pointer;font-weight:500;transition:.2s;}
          .btn-submit:hover{background:#059669;}

          @media (max-width:768px){
            .aqm-content{margin:.5rem;max-width:calc(100% - 1rem);}
            .form-row{grid-template-columns:1fr;}
            .par-item,.columnas-header{grid-template-columns:1fr;gap:.5rem;}
            .columnas-header .acciones-header{display:none;}
          }
        `}</style>
      </div>
    </div>
  );
}
